import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, } from 'react';
import { FlatList, StyleSheet, View, } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import Spacer from 'design-system/Spacer';
import Button, { HEIGHT } from 'design-system/Button';
import { SCROLL_INDICATOR_INSETS, rem } from 'design-system/values';
import ListEmptyView from 'design-system/ListEmptyView';
import ListLoader from 'design-system/ListLoader';
import { getTransactions, isFetchingBankTransactions } from 'actions';
import { selectFeatureFlag } from 'reducers/selectors';
import Amplitude from 'utils/amplitude';
import { getTransactionsFlatlistData, } from 'utils/getTransactions';
import { useReSelector } from 'utils/hooksApi';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useOpenSubscription from 'features/subscriptions/hooks/useOpenSubscription';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import useAdvancedEditTransactions from '../hooks/useAdvancedEditTransactions';
import TransactionRow from './TransactionRow';
import { isWeb } from '../../../constants';
import TransactionEditToast from './TransactionEditToast';
import useNavigateTransactionItem from '../hooks/useNavigateTransactionItem';
const getItemType = (item) => {
    if ('sectionTitle' in item) {
        return 'sectionHeader';
    }
    return 'row';
};
const keyExtractor = (item) => item.id?.toString();
/**
 * needs to be used with
 * dispatch(setTransactionsRequesting);
 * before the screen mounts because of flatlist issue
 * that triggers onReachEnd on mount
 */
const TransactionsList = forwardRef(({ navigation, onScrollY, children, from, to, showAllValues, accountIdsStr, disabledTransactions, showExcludedTransactions = true, reducedTopPadding, ListHeaderComponent, hideUpgradeView, }, ref) => {
    const bankLogoTransaction = useAppSelector((store) => selectFeatureFlag(store, 'bank_logo_transaction')).value;
    const dispatch = useAppDispatch();
    const navigateToTransactionItem = useNavigateTransactionItem();
    const initialFetched = useRef(false);
    const appStore = useAppStore();
    useEffect(() => {
        dispatch(isFetchingBankTransactions('ALL', { from, to }, showExcludedTransactions, accountIdsStr));
        dispatch(getTransactions({
            from,
            to,
            page: 1,
            hasInternalTransactions: !showExcludedTransactions,
            accountIdsStr,
        })).then(() => {
            const { pageCount, page } = appStore.getState().transactions;
            if (pageCount && page && page < pageCount) {
                dispatch(getTransactions({
                    from,
                    to,
                    page: page + 1,
                    hasInternalTransactions: !showExcludedTransactions,
                    accountIdsStr,
                }));
            }
        });
        initialFetched.current = true;
    }, [accountIdsStr, dispatch, from, showExcludedTransactions, to]);
    const { selected, isEditing, selectedObj, onEdit, onSelected, openEditModal, toastVisible, setToastVisible, } = useAdvancedEditTransactions(navigation);
    const store = useReSelector((store) => ({
        transactions: store.transactions.transactionsArray,
        pageCount: store.transactions.pageCount,
        page: store.transactions.page,
        isFetching: store.transactions.isFetching,
        isRefreshing: store.transactions.isRefreshing,
    }));
    const loadMore = useCallback(() => {
        const { isRefreshing, pageCount, page } = store;
        if (!isRefreshing &&
            pageCount &&
            page &&
            page < pageCount &&
            initialFetched.current) {
            dispatch(getTransactions({
                from,
                to,
                page: page + 1,
                hasInternalTransactions: !showExcludedTransactions,
                accountIdsStr,
            }));
        }
    }, [accountIdsStr, from, showExcludedTransactions, store, to]);
    const onEndReached = useCallback(() => {
        loadMore();
    }, [loadMore]);
    useImperativeHandle(ref, () => ({ onEdit }), [onEdit]);
    const onCompare = useCallback((routeWithParams) => {
        navigation.navigateDeprecated(routeWithParams);
        Amplitude.logEvent('Transactions.openCompare', {
            type: routeWithParams.name,
        });
    }, []);
    const openItem = useCallback((item) => {
        if ('sectionTitle' in item)
            return;
        const { isPending } = item;
        if (isEditing) {
            if (isPending)
                return;
            onSelected(item);
            return;
        }
        navigateToTransactionItem(item);
    }, [isEditing, navigateToTransactionItem, onSelected]);
    const { isUnlockedForSpace: isRecurringPaymentsUnlocked } = useShouldShowUpgrade({
        benefitId: 'recurringPayments',
    });
    const { isUnlockedForSpace: isSearchUnlockedForSpace, onPressUpgrade: onPressUpgradeForSearch, } = useShouldShowUpgrade({
        benefitId: 'search',
    });
    const openSubscription = useOpenSubscription();
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const isEditButtonVisible = isEditing && selected.length !== 0;
    const { paddingHorizontalStyle, width, paddingHorizontal } = useAppFrameDimensions(isWeb);
    const renderRow = useCallback(({ item, index }) => {
        const isFirstItem = index === 0;
        return (<TransactionRow item={item} openItem={openItem} onCompare={onCompare} isEditing={isEditing} showAllValues={showAllValues} isSelected={!!selectedObj[item.id]} openSubscription={openSubscription} guessedHomeCountry={guessedHomeCountry} disabledTransactions={disabledTransactions} showUpgradeButton={'showUpgradeButton' in item && item.showUpgradeButton} onPressUpgradeForSearch={onPressUpgradeForSearch} blurSubscriptionValues={!isRecurringPaymentsUnlocked} style={isFirstItem && reducedTopPadding
                ? styles.reducedPaddingTop
                : undefined} hideUpgradeView={hideUpgradeView}/>);
    }, [
        hideUpgradeView,
        disabledTransactions,
        guessedHomeCountry,
        isEditing,
        isRecurringPaymentsUnlocked,
        onCompare,
        onPressUpgradeForSearch,
        openItem,
        openSubscription,
        reducedTopPadding,
        selectedObj,
        showAllValues,
    ]);
    const sectionedData = useMemo(() => {
        if (store.isFetching && !store.transactions.length)
            return [];
        const flashListData = getTransactionsFlatlistData(store.transactions, !isSearchUnlockedForSpace);
        return flashListData;
    }, [isSearchUnlockedForSpace, store.isFetching, store.transactions]);
    const onScroll = useCallback((event) => {
        onScrollY(event.nativeEvent.contentOffset.y);
    }, [onScrollY]);
    const emptyOrLoading = useMemo(() => (<View>
          {store.isFetching ? (<View>
              <ListLoader />
            </View>) : (<>
              <Spacer.H16 />
              <ListEmptyView onCard title="No transactions" subTitle="There are no transactions in your account"/>
            </>)}
        </View>), [store.isFetching]);
    const paddingBottom = useMarginBottom('paddingBottom', isEditButtonVisible ? HEIGHT + rem(16) : undefined);
    const lastItem = sectionedData[sectionedData.length - 1];
    const hasUpgradeButton = lastItem && 'showUpgradeButton' in lastItem && lastItem.showUpgradeButton;
    const customStyles = useMemo(() => ({
        ...(hasUpgradeButton && !isEditButtonVisible ? {} : paddingBottom),
        ...paddingHorizontalStyle,
        // This is a workaround on web because the scroll bar is counted as part of the width
        // which messes up the investment account graph header
        ...(isWeb && {
            width,
            paddingHorizontal: 0,
            marginLeft: paddingHorizontal,
            alignSelf: 'auto',
        }),
    }), [
        hasUpgradeButton,
        isEditButtonVisible,
        paddingBottom,
        paddingHorizontalStyle,
        width,
        paddingHorizontal,
    ]);
    const extraData = useMemo(() => [
        bankLogoTransaction,
        selected,
        openItem,
        isEditing,
        onCompare,
        onSelected,
        openSubscription,
        guessedHomeCountry,
    ], [
        bankLogoTransaction,
        guessedHomeCountry,
        isEditing,
        onCompare,
        onSelected,
        openItem,
        openSubscription,
        selected,
    ]);
    const baseProps = {
        extraData,
        onScroll,
        data: sectionedData,
        renderItem: renderRow,
        scrollEventThrottle: 16,
        onEndReachedThreshold: 5,
        keyExtractor,
        onEndReached,
        ListHeaderComponent,
        ListEmptyComponent: emptyOrLoading,
        contentContainerStyle: customStyles,
        showsVerticalScrollIndicator: true,
        scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
    };
    return (<>
        {isWeb ? (<FlatList {...baseProps}/>) : (<FlashList {...baseProps} getItemType={getItemType}/>)}
        <TransactionEditToast isVisible={toastVisible} onClose={() => setToastVisible(false)}/>
        {isEditButtonVisible ? (<Button brand floating title={`Edit (${selected.length}) transactions`} onPress={openEditModal}/>) : (children)}
      </>);
});
const styles = StyleSheet.create({
    extraMarginTop: {
        marginTop: '70%',
    },
    withHeaderIndicatorMarginTop: {
        marginTop: '45%',
    },
    reducedPaddingTop: {
        paddingTop: rem(16),
    },
});
export default memo(TransactionsList);
